import type { ChangeTracker } from '../changes/ChangeTracker';
import type { DoseMatrix } from '../patient/doseMatrix';
import type { Image3D } from '../patient/image3d';
import type { RigidRegistration } from '../patient/rigidRegistration';
import type { StructureSet } from '../patient/structureset';
import type { ConnectorPatientManifest } from './ConnectorPatientManifest';
import {
    adoptImageRowLabel,
    adoptRegistrationRowApproval,
    adoptRegistrationRowLabel,
    adoptStructureSetRow,
} from './hydratePersistenceRefs';
import {
    asConnectorDatasetId,
    asConnectorVersionToken,
} from './PersistenceRef';
import type {
    PersistedEntityId,
    PersistenceRegistry,
} from './PersistenceRegistry';

/** What a commit added to the session, by kind. */
export type CommittedDatasets = {
    readonly images: readonly Image3D[];
    readonly structureSets: readonly StructureSet[];
    readonly doses: readonly DoseMatrix[];
    readonly registrations: readonly RigidRegistration[];
};

/**
 * Gives a dataset brought into an already open session its connector
 * identity.
 *
 * Load-time hydration matches a whole parsed patient against the manifest by
 * UID, because a parse is all it has to go on. An on-demand load has
 * something better: the user asked for one connector row by its id, so the
 * row is known outright and nothing has to be inferred. The UIDs are used
 * only to pick the parsed object out of the commit.
 *
 * Without this the dataset looks locally created — no ref means the save
 * workflow reports it as a new object and a save writes a second copy of a
 * row the connector already holds.
 *
 * Applies the same row-authoritative overlays hydration does, for the same
 * reason: the row, not the stored file, carries approval and the current
 * label. Unlike hydration, the committed objects are already attached to the
 * session's tracker, so the overlays run inside one `tracker` batch.
 *
 * Returns the id of the dataset it claimed, so the caller can take it off the
 * pending-changes list too — a ref alone makes the tracker call it a
 * modification rather than a creation, which is still one too many.
 */
export function registerLoadedDataset(
    registry: PersistenceRegistry,
    manifest: ConnectorPatientManifest | null | undefined,
    datasetId: string,
    committed: CommittedDatasets,
    tracker: ChangeTracker
): PersistedEntityId | null {
    // A session with no connector backing cannot save, so a ref would mean
    // nothing; a manifest for someone else was already refused at load time.
    if (!manifest || registry.patientRef === null) {
        return null;
    }

    const setRef = (id: PersistedEntityId, version: string): void => {
        registry.setRef(id, {
            datasetId: asConnectorDatasetId(datasetId),
            version: asConnectorVersionToken(version),
        });
    };

    const structureSet = manifest.structureSets.find(
        (entry) => entry.datasetId === datasetId
    );
    if (structureSet) {
        const loaded = pick(
            committed.structureSets,
            structureSet.sopInstanceUid,
            (ss) => ss.dicomUid === structureSet.sopInstanceUid
        );
        if (!loaded) {
            return null;
        }
        setRef(loaded.id, structureSet.version);
        tracker.batch(() => {
            // A set loaded on demand adopts its row exactly as one loaded at
            // open does. Without this the same set would arrive unlocked, or
            // without its recorded origin, depending only on when it was
            // fetched — and a protected structure would be editable.
            adoptStructureSetRow(loaded, structureSet);
        });
        return loaded.id;
    }

    const registration = manifest.registrations.find(
        (entry) => entry.datasetId === datasetId
    );
    if (registration) {
        const loaded = pick(
            committed.registrations,
            registration.sopInstanceUid,
            (reg) => reg.sopInstanceUid === registration.sopInstanceUid
        );
        if (!loaded) {
            return null;
        }
        setRef(loaded.id, registration.version);
        tracker.batch(() => {
            // Label before approval, as at load time: the label write must
            // not trip the approval guard of a state just adopted.
            adoptRegistrationRowLabel(loaded, registration);
            adoptRegistrationRowApproval(loaded, registration);
        });
        return loaded.id;
    }

    const dose = manifest.doses.find((entry) => entry.datasetId === datasetId);
    if (dose) {
        const loaded = pick(
            committed.doses,
            dose.sopInstanceUid,
            (d) => d.sopInstanceUid === dose.sopInstanceUid
        );
        if (!loaded) {
            return null;
        }
        setRef(loaded.id, dose.version);
        return loaded.id;
    }

    const image = manifest.images.find(
        (entry) => entry.datasetId === datasetId
    );
    if (image) {
        const loaded = pick(
            committed.images,
            // An image IS its series, so the UID is always the match key and
            // there is no sole-element fallback to fall back to.
            image.seriesInstanceUid,
            (img) => img.seriesUid === image.seriesInstanceUid
        );
        if (!loaded) {
            return null;
        }
        setRef(loaded.id, image.version);
        tracker.batch(() => adoptImageRowLabel(loaded, image));
        return loaded.id;
    }

    return null;
}

/**
 * The one object of its kind the commit produced, matched against the row.
 *
 * The sole element answers only when the row named no UID to match on — an
 * older backend may not have recorded a SOP Instance UID. Where the row DID
 * name one and the commit disagrees, nothing is claimed: attaching the
 * requested row's identity to different content is how a later save
 * overwrites the wrong Connector row, and a dataset that merely cannot be
 * saved is a far smaller harm than one saved over its neighbour.
 */
function pick<T>(
    committed: readonly T[],
    expectedUid: string | null,
    matches: (candidate: T) => boolean
): T | undefined {
    const matched = committed.filter(matches);
    if (matched.length === 1) {
        return matched[0];
    }
    if (expectedUid !== null) {
        return undefined;
    }
    return committed.length === 1 ? committed[0] : undefined;
}
